import {
  Component,
  ElementRef, EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit, Output,
  QueryList,
  SimpleChanges, ViewChild,
  ViewChildren
} from '@angular/core';
import {TreeNode} from "primeng/api";
import {DomSanitizer} from "@angular/platform-browser";
import {FileUpload, FileUploadHandlerEvent} from "primeng/fileupload";
import {Dialog} from "primeng/dialog";
import {InsurableObjectService} from "../../../cross/user-insurance/api/user-added-insurance-api.service";
import {
  UserAddedInsuranceDocumentDto,
  UserAddedInsuranceDto
} from "../../../cross/user-insurance/types/ds-user-added-insurance.types";

interface DmUploadDocumentFile {
  name: string;
  objectURL: string;
}

export interface UploadDocumentType {

  file: File
  fileBase64: string,
  fileBase64MimeType: string,
  name: string

}

export interface CustomFile extends File {
  progress?: { visible: boolean; value: number; success: boolean; error: boolean; };
  pdfSrc?: string;
}

@Component({
  selector: 'core-upload',
  templateUrl: 'core-upload.component.html',
  styleUrls: ['core-upload.component.scss']
})
export class CoreUploadComponent implements OnInit, OnChanges, OnDestroy {

  @Input() documentTypeId!: number;
  @Input() hideDocumentTypeSelection!: number;

  @Input() layoutVariation: string = 'm-version';

  @Input() showPreview: boolean = true;
  @Output() onDocumentUploaded: EventEmitter<UploadDocumentType[]> = new EventEmitter<UploadDocumentType[]>();
  @Output() onSelect: EventEmitter<File> = new EventEmitter<File>();
  @Output() onClear: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChildren('buttonEl') buttonEl!: QueryList<ElementRef>;
  @ViewChild('cameraInput') cameraInput!: ElementRef<HTMLInputElement>;
  @Input() busy: boolean | undefined;

  maximizable: boolean = true;
  progress: any[] = [];
  documentPreviewSrc: any[] = [];
  selectedFileIndex: number = 0;
  initialized: boolean = false;
  visible: boolean = false;
  showDragNDrop: boolean = true;
  displayValue: string = 'list';
  displayOptions: { icon: string, displayValue: string }[] = [
    {icon: 'pi pi-list', displayValue: 'list'},
    {icon: 'pi pi-grid', displayValue: 'grid'}
  ];
  isMobile = false;

  documentTypeHierarchy: TreeNode[] | undefined;
  selectedDocumentType: TreeNode | undefined;

  constructor(
    protected insurableObjectService: InsurableObjectService,
    private sanitizer: DomSanitizer) {
  }

  uploadedFiles: any[] = [];

  ngOnInit(): void {

    this.initComponent();

    this.refreshComponent();

  }

  /**
   * Initialize Component.
   */
  initComponent() {
    this.isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  }

  async onUploadHandler(event: FileUploadHandlerEvent, fileUploader: FileUpload) {

    this.progress = [];
    const documentUploaded: UploadDocumentType[] = []
    for (let file of event.files) {
      await this.convertFileToBase64AndGetType(file).then(document => {
        documentUploaded.push(document);
      });
    }
    this.onDocumentUploaded.emit(documentUploaded);
    fileUploader.files = [];
    fileUploader.cd.detectChanges()

  }


  onImageMouseOver(file: DmUploadDocumentFile) {
    this.buttonEl.toArray().forEach(el => {
      el.nativeElement.id === file.name ? el.nativeElement.style.display = 'flex' : null;
    })
  }

  onImageMouseLeave(file: DmUploadDocumentFile) {
    this.buttonEl.toArray().forEach(el => {
      el.nativeElement.id === file.name ? el.nativeElement.style.display = 'none' : null;
    })
  }

  removeImage(event: Event, file: any) {
    event.stopPropagation();
    this.uploadedFiles = this.uploadedFiles.filter(i => i !== file);
  }

  /**
   * Refresh Component.
   */
  refreshComponent() {
    this.initialized = true;
  }

  convertFileToBase64AndGetType(file: File): Promise<UploadDocumentType> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      const updatedName = this.cropFilename(file.name);
      reader.onload = function (event) {
        const result = event.target?.result;
        const base64 = typeof result === 'string' ? result.split(',')[1] : ''; // Splitting and getting the Base64 content

        resolve({
          file: file,
          fileBase64: base64,
          fileBase64MimeType: file.type,
          name: updatedName
        });
      };

      reader.onerror = function (error) {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }

  cropFilename(filename: string, maxLength: number = 50): string {
    const extension = filename.slice(((filename.lastIndexOf(".") - 1) >>> 0) + 2);
    const baseName = filename.slice(0, filename.length - extension.length - 1);
    const croppedBaseName = baseName.length + extension.length + 1 > maxLength ?
      baseName.slice(0, maxLength - extension.length - 1) :
      baseName;
    return `${croppedBaseName}.${extension}`;
  }

  onSelectHandler(event: any) {
    this.onSelect.emit(event.files)
    // this.showDragNDrop = false;
    if (!event?.files?.length) {
      this.onClear.emit(true);
    }
    for (let file of event.files) {
      const selectedFile = file;
      const reader = new FileReader();

      // Read the PDF file as an ArrayBuffer
      reader.readAsArrayBuffer(selectedFile);

      reader.onload = (e: any) => {
        // Convert the ArrayBuffer to a data URL
        const pdfDataUrl = e.target.result;
        file.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl('data:application/pdf;base64,' + this.arrayBufferToBase64(pdfDataUrl))

      };

    }

  }


  arrayBufferToBase64(buffer: ArrayBuffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
  }

  preview(file: File) {
    const selectedFile = file;
    const reader = new FileReader();

    // Read the PDF file as an ArrayBuffer
    reader.readAsArrayBuffer(selectedFile);

    reader.onload = (e: any) => {
      // Convert the ArrayBuffer to a data URL
      const pdfDataUrl = e.target.result;
      this.documentPreviewSrc = [];
      this.documentPreviewSrc.push(this.sanitizer.bypassSecurityTrustResourceUrl('data:application/pdf;base64,' + this.arrayBufferToBase64(pdfDataUrl)));
    }
    this.visible = true;
  }

  removeFile(event: any, file: File, uploader: FileUpload) {
    const index = uploader.files.indexOf(file);
    uploader.remove(event, index);
    this.onSelectHandler(uploader);
  }

  changeView(selectedOption: string) {
    this.displayValue = selectedOption;
  }

  showDialogMaximized(dialog: Dialog) {
    if (this.displayValue == 'grid') {
      this.maximizable = false;
      dialog.maximize();
    } else {
      this.maximizable = true;
    }
  }

  nextDocument(totalLength: number) {
    this.selectedFileIndex = Math.min(this.selectedFileIndex + 1, totalLength - 1);

  }

  previousDocument() {
    this.selectedFileIndex = Math.max(this.selectedFileIndex - 1, 0);
  }

  /**
   * Process changes within Binding.
   * @param changes
   */
  ngOnChanges(changes: SimpleChanges): void {

    if (!this.initialized) {
      return;
    }

  }

  /**
   * Destroy component.
   */
  ngOnDestroy(): void {
  }


  handleWindowOpened(event: boolean) {
    this.visible = false;
  }


  openCamera(): void {
    this.cameraInput.nativeElement.click();
  }
  onCapture(event: Event, uploader: FileUpload): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (files && files.length > 0) {
      const file = files[0];

      // Trigger upload handler with correct type
      this.onUploadHandler({ files: [file] }, uploader);
    }

    input.value = ''; // Reset input to allow repeat capture
  }


}
